import React from "react";

interface PublishedPostBannerProps {
  /** Live URL of the published post. */
  url: string;
  /** Where it went (e.g. "blog.example.com"). Omitted = "your website". */
  siteLabel?: string;
  /** Optional spacing/layout classes from the caller (e.g. "mb-4"). */
  className?: string;
}

// Brand-tinted surfaces derived from the active theme's primary color. Uses
// color-mix (the same technique as the app's theme overrides) so the alpha is
// valid CSS even though the theme color is stored as a hex variable, and so it
// adapts automatically to light/dark.
const surface = "color-mix(in srgb, var(--color-primary) 7%, transparent)";
const surfaceBorder = "color-mix(in srgb, var(--color-primary) 22%, transparent)";
const iconSurface = "color-mix(in srgb, var(--color-primary) 14%, transparent)";
const linkBorder = "color-mix(in srgb, var(--color-primary) 30%, transparent)";

/**
 * Premium, on-brand confirmation shown once a blog is live on a site.
 * Centralized so the "View live post" treatment is identical everywhere it
 * appears (the chat editor and the Content → Blogs editor share this).
 */
export const PublishedPostBanner: React.FC<PublishedPostBannerProps> = ({
  url,
  siteLabel,
  className = "",
}) => (
  <div
    className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${className}`}
    style={{ backgroundColor: surface, borderColor: surfaceBorder }}
  >
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-theme-primary"
      style={{ backgroundColor: iconSurface }}
    >
      <span className="material-icons text-[16px]">check</span>
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-theme-fg">Published to {siteLabel || "your website"}</p>
      <p className="text-xs text-theme-fg-muted">Your post is live.</p>
    </div>
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold text-theme-primary transition-opacity hover:opacity-80"
      style={{ borderColor: linkBorder }}
    >
      View live post
      <span className="material-icons text-[14px]">open_in_new</span>
    </a>
  </div>
);

export default PublishedPostBanner;
